import * as Sentry from "@sentry/react";
import {
  REALTIME_POSTGRES_CHANGES_LISTEN_EVENT,
  RealtimeChannel,
  RealtimePostgresChangesFilter,
  RealtimePostgresChangesPayload,
  RealtimePostgresInsertPayload,
  RealtimePostgresUpdatePayload,
  SupabaseClient,
} from "@supabase/supabase-js";

/**
 * Utility type to determine the payload type for a given Postgres changes event.
 */
export type PayloadForPostgresChangeEvent<
  TEvent extends REALTIME_POSTGRES_CHANGES_LISTEN_EVENT,
  TTableRow extends Record<string, unknown>,
> =
  TEvent extends REALTIME_POSTGRES_CHANGES_LISTEN_EVENT.ALL ? RealtimePostgresChangesPayload<TTableRow>
  : TEvent extends REALTIME_POSTGRES_CHANGES_LISTEN_EVENT.INSERT ? RealtimePostgresInsertPayload<TTableRow>
  : TEvent extends REALTIME_POSTGRES_CHANGES_LISTEN_EVENT.UPDATE ? RealtimePostgresUpdatePayload<TTableRow>
  : TEvent extends REALTIME_POSTGRES_CHANGES_LISTEN_EVENT.DELETE ? RealtimePostgresUpdatePayload<TTableRow>
  : never;

/**
 * This is the type returned from the 'system' realtime event. It is undocumented, but this is what it looks like via
 * inspection.
 */
interface SystemMessage<TStatus extends "ok" | "error" = "ok" | "error"> {
  message: string;
  status: TStatus;
  extension: string;
  channel: string;
  error: TStatus extends "error" ? string : undefined;
}

/**
 * This class wraps a supabase realtime channel to provides some logging, error handling and cleaner APIs.
 */
export class SupabaseRealtimeChannel implements AsyncDisposable {
  private readonly channel: RealtimeChannel;

  constructor(
    supabase: SupabaseClient,
    private readonly channelName: string,

    /**
     * Callback to execute when an error occurs in a `system` message, or in any of the subscribed event callbacks.
     * By default, errors are logged to the console and reported to Sentry.
     */
    private readonly onError: (error: Error) => void = (error: Error) => {
      console.error(`An error occurred in the ${channelName} realtime channel:`, error);
      Sentry.captureException(error);
    },
  ) {
    this.channel = supabase.channel(channelName);

    // Add an even listener to log status updates and errors
    this.channel.on("system", {}, (payload: SystemMessage) => {
      if (payload.status === "error") {
        onError(new Error(`Supabase realtime system message error: ${payload.message}`));
      } else {
        console.debug(payload);
      }
    });
  }

  getInternalChannel(): RealtimeChannel {
    return this.channel;
  }

  /**
   * Wraps {@link typeof RealtimeChannel.on} with error handling using the `onError` callback provided in this class constructor
   *
   * @param filter - The filter to apply to the Postgres changes event
   * @param callback - The callback to execute when a matched change event occurs. Note that the payload passed to this
   *                   callback will never contain the `errors` property, as this is handled internally by this class.
   */
  public onPostgresChange<
    TEvent extends REALTIME_POSTGRES_CHANGES_LISTEN_EVENT,
    TTableRow extends Record<string, unknown>,
  >(
    filter: RealtimePostgresChangesFilter<TEvent>,
    callback: (payload: PayloadForPostgresChangeEvent<TEvent, TTableRow>) => void,
  ) {
    // Add the event listener to the channel, wrapping the callback to handle errors
    this.channel.on<TTableRow>(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
      "postgres_changes" as any,
      filter,
      (payload: PayloadForPostgresChangeEvent<TEvent, TTableRow>) => {
        try {
          console.debug(`Received realtime update:`, payload);

          // Fail if the payload contains errors
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Type definition from library is wrong
          if (payload.errors?.length) {
            this.onError(new Error(`Received errors from realtime update: ${JSON.stringify(payload.errors)}`));
            return;
          } else {
            // Otherwise, execute the provided callback with the payload
            callback(payload);
          }
        } catch (err) {
          this.onError(
            new Error(`Error processing realtime update: ${err instanceof Error ? err.message : String(err)}`, {
              cause: err,
            }),
          );
        }
      },
    );

    // Return the current instance to allow method chaining
    return this;
  }

  public async subscribeAsync() {
    return await new Promise<"SUBSCRIBED">((resolve, reject) => {
      this.channel.subscribe((status, err) => {
        if (err) {
          reject(err);
        } else {
          console.debug(`Subscription update for realtime channel '${this.channelName}': ${status}`);
          resolve(status as "SUBSCRIBED");
        }
      });
    });
  }

  async [Symbol.asyncDispose](): Promise<void> {
    const result = await this.channel.unsubscribe();
    if (result !== "ok") console.warn(`Failed to unsubscribe from Supabase realtime channel: ${result}`);
  }
}
